import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import apiService from "../../../Services/authenticationServices";
import { useCheOrders } from "./CheOrders";
import { convertUTCDate } from "../../../Utils/utilServices";
import { fmtDateTime } from "../../../Utils/dateFormat";
import { toast } from 'react-toastify';
import split from '../../../Assets/split.svg'
import bulkOrderIcon from '../../../Assets/BulkOrder.png'
import AlternatePopup from "../B2bOrders/preferedMedicine";

const AccordionItem = ({ data, index, isOpen, onToggle, onClose }) => {
  const [enquiryDetails, setEnquiryDetails] = useState([]);
  const [selectedRow, setSelectedRow] = useState({});
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isRemarksModalOpen, setIsRemarksModalOpen] = useState(false);
  const [remarksText, setRemarksText] = useState('');
  const [altternatePopup, setaltternatePopup] = useState(false);
  const [isBulkOrderModalOpen, setIsBulkOrderModalOpen] = useState(false);
  const [isBulkOrderEligible, setIsBulkOrderEligible] = useState(false);
  const scrollRef = useRef(null);

  const slaList = [
    { name: '0 -12 Hours', value: 1 },
    { name: '12-24 Hours', value: 2 },
    { name: '24-36 Hours', value: 3 },
    { name: '36-48 Hours', value: 4 },
    { name: '48-60 Hours', value: 5 },
    { name: '60-72 Hours', value: 6 },
    { name: '72 Hrs-1 Week', value: 7 },
    { name: 'Cannot be supplied', value: 8 },
    { name: 'CureBay Preferred Medicine ', value: 9 },
  ];

  const bgColor = index % 2 === 0 ? "bg-[#f6f5f1]" : "bg-[#FFFFFF]";

  const headerFields = [
    { label: "Enquiry Date", key: "enquiryDateTime", width: "sm:w-[10%]" },
    { label: "Enquiry ID", key: "id", width: "sm:w-[5%]" },
    { label: "Patient ID", key: "patientId", width: "sm:w-[10%]" },
    { label: "Patient Name", key: "patientName", width: "sm:w-[10%]" },
    { label: "Mobile No", key: "patientMobile", width: "sm:w-[5%]" },
    { label: "Patient Address", key: "patientAddress", width: "sm:w-[20%]" },
    { label: "Eclinic", key: "cheCenterName", width: "sm:w-[5%]" },
    { label: "Eclinic Address", key: "shippingAddress", width: "sm:w-[20%]" },
    { label: "Amount", key: "totalAmount", width: "sm:w-[5%]", fallbackKey: "amount" },
  ];

  const cols = [
    { field: 'drugName', header: 'Drug Name' },
    { field: 'drugCode', header: 'Drug Code' },
    { field: 'drugsInfoTypeofsell', header: 'Package Size' },
    { field: 'quantity', header: 'Quantity' },
    { field: 'unitPrice', header: 'Original Unit Price' },
    { field: 'totalAmount', header: 'Payable Amount' },
    { field: 'alternateQuantity', header: 'Modified Quantity', mandatory: true },
    { field: 'alternateUnitPrice', header: 'Revised Unit Price', mandatory: true },
    { field: 'deliverySla', header: 'Delivery SLA', mandatory: true },
    { field: 'alternateMedicineName', header: 'CureBay Preferred Medicine' },
    { field: '', header: 'Action' },
    { field: 'remarks', header: 'Remarks' },
  ];

  useEffect(() => {
    if (isOpen && scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    try {
      const payload = { enquiryId: data.id, status: 1 };
      const response = await apiService.getEnquiryDetails(payload);
      const transformedList = response.data.successList.map(element => {
        return {
          ...element,
          alternateQuantity: element.alternateQuantity ?? element.quantity,
          alternateUnitPrice: element.alternateUnitPrice ?? element.unitPrice,
          deliverySla: element.deliverySla ? +element.deliverySla : +element.defaultSla || 3,
        };
      });

      // Check if bulk order is eligible from the API response
      if (response.data.bulkOrderEligible === 'Y') {
        setIsBulkOrderEligible(true);
      } else {
        setIsBulkOrderEligible(false);
      }

      setEnquiryDetails(transformedList);
    } catch (err) {
      console.error("Error fetching enquiry details", err);
      setIsBulkOrderEligible(false);
    }
  };

  const openRemarksModal = () => {
    setRemarksText(selectedRow?.remarks || '');
    setIsRemarksModalOpen(true);
  };

  const handleRemarksSave = () => {
    if (selectedRow) {
      const updated = enquiryDetails.map((item) =>
        item.id === selectedRow.id ? { ...item, remarks: remarksText } : item
      );
      setEnquiryDetails(updated);
      setSelectedRow({ ...selectedRow, remarks: remarksText });
      setIsRemarksModalOpen(false);
    }
  };

  const handleBulkOrderConfirm = async () => {
    try {
      const payload = {
        enquiryId: data.id,
        userId: localStorage.getItem('userCode')
      };

      const response = await apiService.bulkOrderEnquiry(payload);

      if (response?.data?.responseCode === '200') {
        submitOrder();
        onClose();
      } else {
        toast.error(response?.data?.message || 'Failed to process bulk order');
      }
    } catch (error) {
      console.error('Error processing bulk order:', error);
      toast.error('Failed to process bulk order');
    } finally {
      setIsBulkOrderModalOpen(false);
    }
  };

  const submitOrder = async () => {
    try {
      let overAllStatus = false;
      let payload = JSON.parse(JSON.stringify(enquiryDetails));

      payload.forEach(element => {
        if (element.deliverySla != 8 && element.deliverySla != 9) {
          if (!element.deliverySla || !element.alternateQuantity || !element.alternateUnitPrice) {
            element.required = true;
            overAllStatus = true;
          } else {
            element.required = false;
          }
        } else {
          element.required = false;
        }

        if (!overAllStatus) {
          element.modifiedBy = localStorage.getItem('userCode');
          element.modifiedDate = convertUTCDate(new Date, 'YYYY-MM-DD HH:mm:ss');
          element.createdBy = element.createdBy;
          element.createdDate = convertUTCDate(new Date, 'YYYY-MM-DD HH:mm:ss');
          element.status = element.deliverySla == 8 ? 10 : 2;
          element.modifiedMembershipDiscountedPriceAmount = 0;
          element.discountAmount = 0;
          element.modifiedBy = localStorage.getItem('userCode');
          element.enquiryId = (data.id).toString();
        }
      });

      if (overAllStatus) {
        toast.error('Please fill all mandatory fields');
        setEnquiryDetails(payload);
        return;
      }

      // MDM drug-master lookup (getDrugMasterDetails) removed per request — the
      // Medicine ID is no longer required for submission, so we no longer call
      // https://mdm.curebay.com/.../cureBayDrugMaster/ and submit directly.

      await completeSubmit(payload);
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit order');
    }
  };

  const completeSubmit = async (payload) => {
    try {
      payload[0].orderStatus = 2;
      const res = await apiService.submitEnquiryDetails(payload);

      if (res.data.responseCode === '200') {
        toast.success('Order Submitted Successfully');
        onClose();
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit order');
    }
  };

  const saveChanges = async () => {
    try {
      let payload = JSON.parse(JSON.stringify(enquiryDetails));
      payload.forEach(element => {
        element.discountAmount = 0;
        element.enquiryId = (data.id).toString();
        element.prescriptionRequired = 'Y';
      });

      const res = await apiService.updateEnquiryDetails(payload);
      if (res.data.responseCode === '200') {
        toast.success('Changes Saved Successfully');
        onClose();
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to save changes');
    }
  };

  const onSlaChange = (e, row, rowIndex) => {
    const selectedSla = parseInt(e.target.value);
    const updatedList = [...enquiryDetails];

    if (selectedSla === 9) {
      if (!row.alternateMedicineName) {
        setaltternatePopup(true);
      }
    } else {
      if (row.alternateMedicineName) {
        const deleteIndex = enquiryDetails.findIndex(item => item.drugCode === row.alternateMedicineCode);
        if (deleteIndex !== -1) {
          updatedList.splice(deleteIndex, 1);
        }

        updatedList[rowIndex] = {
          ...updatedList[rowIndex],
          alternateMedicineCode: '',
          alternateMedicineName: '',
          alternateUnitPrice: row.unitPrice,
          alternateQuantity: row.quantity,
        };
      }
    }

    if (selectedSla === 8 || selectedSla === 9) {
      updatedList[rowIndex] = {
        ...updatedList[rowIndex],
        alternateQuantity: 0,
        alternateUnitPrice: 0,
      };
    }

    updatedList[rowIndex].deliverySla = selectedSla;
    setEnquiryDetails(updatedList);
  };

  const changeQty = (data, e, index) => {
    const updatedItems = enquiryDetails.map(item => ({ ...item }));

    if (updatedItems[index].deliverySla === 8 && +e !== 0) {
      updatedItems[index].deliverySla = null;
    }

    let qtyCheck = +e;
    updatedItems.forEach((element, i) => {
      if (i !== index && element.drugCode === data.drugCode) {
        qtyCheck += +element.alternateQuantity || 0;
      }
    });

    if (qtyCheck > data.quantity && +e !== 0) {
      toast.error('Modified quantity cannot exceed original quantity');
      updatedItems[index].alternateQuantity = 0;
      setEnquiryDetails(updatedItems);
      return;
    }

    updatedItems.forEach(element => {
      if (element.drugCode === data.drugCode) {
        element.maxreached = qtyCheck >= element.quantity ? 'Y' : undefined;
      }
    });

    const totalAmount = (+e * +data.alternateUnitPrice).toFixed(2);

    updatedItems[index] = {
      ...updatedItems[index],
      alternateQuantity: +e,
      modifiedTotalAmount: totalAmount,
      landingPrice: totalAmount,
    };

    setEnquiryDetails(updatedItems);
    setSelectedRow(updatedItems[index]);
    setSelectedIndex(index);
  };

  const changePrice = (data, e, index) => {
    const updatedItems = [...enquiryDetails];
    const totalAmount = (+data.alternateQuantity * +e).toFixed(2);

    updatedItems[index] = {
      ...updatedItems[index],
      alternateUnitPrice: +e,
      modifiedTotalAmount: totalAmount,
      landingPrice: totalAmount,
    };

    setEnquiryDetails(updatedItems);
    setSelectedRow(updatedItems[index]);
    setSelectedIndex(index);
  };

  const handleRowClick = (row, index) => {
    setSelectedRow(row);
    setSelectedIndex(index);
  };

  const handleAddAlternate = (selectedAlternate, alternateList) => {
    if (!selectedAlternate) {
      return toast.error('Please select alternative medicine');
    }

    setEnquiryDetails(prevDetails => {
      const updatedDetails = [...prevDetails];
      const originalIndex = selectedIndex;

      if (originalIndex === -1) return prevDetails;

      const selected = alternateList.find(e =>
        e.id.includes(selectedAlternate.id || selectedAlternate)
      );

      if (!selected) return prevDetails;

      updatedDetails[originalIndex] = {
        ...updatedDetails[originalIndex],
        alternateMedicineName: selected.medicineName,
        alternateMedicineCode: selected.id,
        deliverySla: 9
      };

      const alternateEntry = {
        ...updatedDetails[originalIndex],
        id: null,
        drugName: selected.medicineName,
        drugCode: selected.id,
        drugComposition: selected.composition,
        drugTypeOfSell: selected.medicineTypeOfSell,
        unitPrice: selected.salePrice,
        alternateUnitPrice: selected.salePrice,
        quantity: updatedDetails[originalIndex].quantity,
        alternateQuantity: updatedDetails[originalIndex].alternateQuantity,
        deliverySla: 0,
        isAlternate: true,
        originalDrugCode: updatedDetails[originalIndex].drugCode
      };

      alternateEntry.modifiedTotalAmount = (+alternateEntry.alternateQuantity * +alternateEntry.alternateUnitPrice).toFixed(2);
      alternateEntry.alternateMedicineName = '';
      updatedDetails.push(alternateEntry);

      return updatedDetails;
    });

    setaltternatePopup(false);
  };
  const copy = (e, i) => {
    setEnquiryDetails(prevDetails => {
      const newDetails = [...prevDetails];
      const copiedItem = { ...newDetails[i] };

      newDetails[i] = {
        ...newDetails[i],
        copiedFrom: 'Y'
      };

      const newItem = {
        ...copiedItem,
        id: null,
        referenceIndex: copiedItem.referenceIndex || i,
        referMqty: copiedItem.referMqty || copiedItem.alternateQuantity,
        referqty: copiedItem.referqty || copiedItem.quantity,
        copiedqty: newDetails[i].alternateQuantity,
        copiedunitprice: copiedItem.alternateUnitPrice / copiedItem.alternateQuantity,
        copiedtotalunitPrice: copiedItem.totalAmount / copiedItem.alternateQuantity,
        alternateQuantity: 0,
        copied: 'Y',
        copiedindex: i
      };
      delete newItem.copiedFrom;

      const updatedDetails = [...newDetails, newItem];
      const drugCodeItems = updatedDetails.filter(item => item.drugCode === e.drugCode);
      const totalQty = drugCodeItems.reduce((sum, item) => sum + (+item.alternateQuantity || 0), 0);

      return updatedDetails.map(item => {
        if (item.drugCode === e.drugCode) {
          return {
            ...item,
            maxreached: totalQty >= item.quantity ? 'Y' : undefined
          };
        }
        return item;
      });
    });
  };
  const getStatus = (value) => {
    const slaItem = slaList.find(item => item.value === value || '');
    return slaItem?.name ?? ''
  };

  useEffect(() => {
    if (!isOpen) {
      setIsRemarksModalOpen(false);
      setaltternatePopup(false);
      setIsBulkOrderModalOpen(false);
      setSelectedRow({});
      setSelectedIndex(null);
      setRemarksText('');
    }
  }, [isOpen]);

  return (
    <>
      <div ref={scrollRef} className={`border rounded ${bgColor} my-3`}>
        <div
          onClick={onToggle}
          className="cursor-pointer px-2 sm:px-4 flex justify-between items-center"
        >
          <div className="flex-1 sm:flex-initial min-w-0 flex flex-wrap sm:flex-nowrap overflow-auto text-xs p-2 sm:p-4 gap-x-3 gap-y-2 sm:gap-5">
            {headerFields.map(({ label, key, width, fallbackKey }) => {
              let value = data[key] ?? (fallbackKey && data[fallbackKey]);

              if (key === 'enquiryDateTime' && value) {
                value = fmtDateTime(value, { zone: "utc-local" });
              }

              return (
                <span key={key} className={`w-[calc(50%-6px)] ${width} break-words`}>
                  <span className="text-gray-500">{label}:</span> {value || '-'}
                </span>
              );
            })}
          </div>
          <span className="text-gray-600 text-xl">
            {isOpen ? <HiChevronUp /> : <HiChevronDown />}
          </span>
        </div>

        {isOpen && (
          <>
            <div className="h-[1px] bg-gray-300 w-full" />
            <div className="p-4 pb-8 bg-white overflow-auto">
              <div className="flex flex-wrap sm:flex-nowrap overflow-auto gap-4 mb-4 text-sm">
                {(
                  <div className="w-full sm:w-[15%] break-words">
                    <p className="text-gray-500">
                      Delivery SLA: <span>{getStatus(data?.defaultSla || 3)}</span>
                    </p>
                  </div>
                )}
                {(
                  <div className="w-full sm:w-[15%] break-words">
                    <p className="text-gray-500">
                      Ship To: <span>{data.shipTo}</span>
                    </p>
                  </div>
                )}
                {(
                  <div className="w-full sm:w-[20%] break-words">
                    <p className="text-gray-500">
                      Shipping Name: <span>{data.shippingName}</span>
                    </p>
                  </div>
                )}
                {(
                  <div className="w-full sm:w-[50%] break-words">
                    <p className="text-gray-500">
                      Shipping Address: <span>{data.shippingAddress}</span>
                    </p>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap justify-between gap-2 mb-3">
                <div className="flex gap-4">
                  <button
                    className="bg-[#336b3b] text-white px-4 py-2 rounded-md"
                    onClick={submitOrder}
                  >
                    Submit
                  </button>
                  {data.bulkOrderFlag === "Y" ? (
                    <div className="flex items-center text-[#2e4373] font-medium">
                      <img
                        src={bulkOrderIcon}
                        alt="Bulk Order Icon"
                        className="h-6 w-6 mr-1"
                      />
                      Marked as Bulk Order
                    </div>
                  ) : (
                    isBulkOrderEligible && (
                      <>
                        <button
                          className="bg-[#2e4373] text-white px-4 py-2 rounded-md"
                          onClick={() => setIsBulkOrderModalOpen(true)}
                        >
                          Bulk Order Submit
                        </button>

                        <div className="relative group top-3 -ml-2">
                          <div className="flex items-center justify-center h-5 w-5 rounded-full border border-gray-600 text-gray-600 cursor-pointer">
                            <span className="text-sm font-bold italic">i</span>
                          </div>

                          <div className="absolute left-0 bottom-10 w-64 sm:w-96 bg-gray-600 text-white text-sm rounded p-2 hidden group-hover:block z-20">
                            • Orders with over 10 SKUs are eligible.
                            <br />
                            • Any SKU with quantity above 10 is eligible.
                          </div>
                        </div>
                      </>
                    )
                  )}
                </div>
                <button
                  className="bg-[#336b3b] text-white px-4 py-2 rounded-md"
                  onClick={saveChanges}
                >
                  Save Changes
                </button>
              </div>

              <div className="overflow-x-auto border border-[#1d2f5b] rounded-md">
                <div className="min-w-full inline-block align-middle">
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm text-left">
                      <thead className="bg-[#1d2f5b] text-white sticky top-0 z-10">
                        <tr>
                          {cols.map((column) => (
                            <th
                              key={column.field || column.header}
                              className="p-2 font-semibold whitespace-nowrap"
                            >
                              {column.header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {enquiryDetails.map((item, rowIndex) => (
                          <tr
                            key={rowIndex}
                            className={`border-t cursor-pointer ${selectedRow?.id !== item.id ? "hover:bg-blue-100" : ""} ${selectedRow?.id === item.id ? "bg-blue-100" : ""}`}
                            onClick={() => handleRowClick(item, rowIndex)}
                          >
                            {cols.map((col, colIndex) => {
                              if (col.field === '') {
                                return (
                                  <td key={colIndex} className="p-2">
                                    {item.alternateQuantity < item.quantity &&
                                      item.alternateUnitPrice &&
                                      !item.copiedFrom &&
                                      !item.maxreached && (
                                        <img
                                          src={split}
                                          alt="Split"
                                          width="30"
                                          style={{ transform: "rotate(90deg)", cursor: "pointer" }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            copy(item, rowIndex);
                                          }}
                                        />
                                      )}
                                  </td>
                                );
                              }

                              if (col.field === 'alternateQuantity') {
                                return (
                                  <td key={colIndex} className="p-2">
                                    <input
                                      type="number"
                                      className="w-full p-2 rounded border"
                                      value={item.alternateQuantity}
                                      onChange={(e) => {
                                        const updated = { ...item, alternateQuantity: e.target.value };
                                        changeQty(updated, e.target.value, rowIndex);
                                      }}
                                      disabled={item.copiedFrom === 'Y' || item.deliverySla === 9}
                                      style={{
                                        borderColor: col.mandatory && (!item.alternateQuantity || +item.alternateQuantity === 0)
                                          ? 'red' : ''
                                      }}
                                    />
                                  </td>
                                );
                              }

                              if (col.field === 'alternateUnitPrice') {
                                return (
                                  <td key={colIndex} className="p-2">
                                    <input
                                      type="number"
                                      className="w-full p-2 rounded border"
                                      value={item.alternateUnitPrice}
                                      onChange={(e) => {
                                        const updated = { ...item, alternateUnitPrice: e.target.value };
                                        changePrice(updated, e.target.value, rowIndex);
                                      }}
                                      disabled={item.copiedFrom === 'Y' || item.deliverySla === 9}
                                      style={{
                                        borderColor: col.mandatory && (!item.alternateUnitPrice || +item.alternateUnitPrice === 0)
                                          ? 'red' : ''
                                      }}
                                    />
                                  </td>
                                );
                              }

                              if (col.field === 'deliverySla') {
                                return (
                                  <td key={colIndex} className="p-2">
                                    <select
                                      className="w-full border p-2 rounded"
                                      value={item.deliverySla}
                                      onChange={(e) => onSlaChange(e, item, rowIndex)}
                                      style={{
                                        borderColor: col.mandatory && !item.deliverySla
                                          ? 'red' : ''
                                      }}
                                    >
                                      <option value="">Select SLA</option>
                                      {slaList.map((sla) => (
                                        <option key={sla.value} value={sla.value}>
                                          {sla.name}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                );
                              }

                              if (col.field === 'remarks') {
                                return (
                                  <td key={colIndex} className="p-2">
                                    {item.remarks ? (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedRow(item);
                                          openRemarksModal();
                                        }}
                                        className="text-blue-600 underline flex items-center gap-1"
                                      >
                                        <span>View</span>
                                        <svg
                                          className="w-4 h-4"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          viewBox="0 0 24 24"
                                        >
                                          <path d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                      </button>
                                    ) : (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedRow(item);
                                          openRemarksModal();
                                        }}
                                        className="text-green-600 underline"
                                      >
                                        + Add
                                      </button>
                                    )}
                                  </td>
                                );
                              }

                              return (
                                <td key={colIndex} className="p-2">
                                  {item[col.field] ?? '-'}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Existing modals */}
      {isRemarksModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-2 sm:p-0">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-[400px]">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Add Remarks</h2>
              <button
                onClick={() => setIsRemarksModalOpen(false)}
                className="text-gray-600 hover:text-black"
              >
                ✕
              </button>
            </div>
            <textarea
              className="w-full border rounded p-2 min-h-[100px]"
              value={remarksText}
              onChange={(e) => setRemarksText(e.target.value)}
            />
            <div className="mt-4 text-right">
              <button
                onClick={handleRemarksSave}
                className="bg-[#336b3b] text-white px-4 py-2 rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {altternatePopup && (
        <AlternatePopup
          visible={altternatePopup}
          onHide={() => setaltternatePopup(false)}
          orderItem={selectedRow.drugCode}
          onAddAlternate={handleAddAlternate}
        />
      )}

      {/* Bulk Order Confirmation Modal */}
      {isBulkOrderModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-2 sm:p-0">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-[400px]">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Bulk Orders</h2>
              <button
                onClick={() => setIsBulkOrderModalOpen(false)}
                className="text-gray-600 hover:text-black"
              >
                ✕
              </button>
            </div>
            <p className="mb-6">Are you sure that you would like to submit this Enquiry as a bulk order?</p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setIsBulkOrderModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkOrderConfirm}
                className="bg-[#336b3b] text-white px-4 py-2 rounded"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

const CheEnquiry = forwardRef((props, ref) => {
  const [openIndex, setOpenIndex] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [sortOrder, setSortOrder] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const selectedDateRange = 1;
  const [hospitalId, setHospitalId] = useState(null);
  const { filter } = useCheOrders();

  const getDateRange = (days) => {
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - (days - 1));
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { fromDate: fmt(fromDate), toDate: fmt(toDate) };
  };

  const fetchAnalytics = async (days, hospId, customFromDate, customToDate) => {
    if (!hospId) return;
    try {
      const { fromDate, toDate } = (customFromDate && customToDate)
        ? { fromDate: customFromDate, toDate: customToDate }
        : getDateRange(days);
      const response = await apiService.getEnquiryAnalytics(hospId, fromDate, toDate);
      if (response?.data) {
        setAnalyticsData(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch analytics', err);
    }
  };

  useEffect(() => {
    if (hospitalId) fetchAnalytics(selectedDateRange, hospitalId);
  }, [hospitalId]);

  const fetchData = useCallback(async (pageNo = 1) => {
    try {
      const payload = {
        status: 1,
        pageNumber: pageNo,
        orderBy: sortOrder === 0 ? 'ASC' : 'DESC',
        ...Object.fromEntries(
          Object.entries(filter).filter(
            ([key, val]) => val !== null && val !== undefined && val !== "" && key !== 'fromDate' && key !== 'toDate'
          )
        ),
      };

      const response = await apiService.getEnquiryOrders(payload);
      const newEnquiries = response.data.successList || [];

      if (newEnquiries[0]?.hospitalId) {
        setHospitalId(newEnquiries[0].hospitalId);
      }

      setEnquiries(newEnquiries);
      setCurrentPage(pageNo);
      setHasNextPage(newEnquiries.length > 0);
      setOpenIndex(null);
    } catch (err) {
      console.error("Failed to fetch enquiry orders", err);
      setEnquiries([]);
      setHasNextPage(false);
    }
  }, [filter, sortOrder, selectedDateRange]);

  const handleNextPage = () => {
    fetchData(currentPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      fetchData(currentPage - 1);
    }
  };

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  useEffect(() => {
    if (openIndex !== null) return;
    const interval = setInterval(() => {
      fetchData(currentPage);
      fetchAnalytics(selectedDateRange, hospitalId, filter.fromDate, filter.toDate);
    }, 30000);
    return () => clearInterval(interval);
  }, [openIndex, currentPage, fetchData, selectedDateRange, hospitalId, filter]);

  useImperativeHandle(ref, () => ({
    onSearch: () => {
      fetchData(1);
      fetchAnalytics(selectedDateRange, hospitalId, filter.fromDate, filter.toDate);
    },
  }));

  const handleToggle = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  const handleSortChange = (value) => {
    setSortOrder(value);
  };

  const analyticsCards = [
    { label: 'Total Received', key: 'totalReceived', color: 'bg-blue-50 border-blue-200', textColor: 'text-blue-700', numColor: 'text-blue-600' },
    { label: 'Total Completed', key: 'totalCompleted', color: 'bg-green-50 border-green-200', textColor: 'text-green-700', numColor: 'text-green-600' },
    { label: 'Total Pending', key: 'totalPending', color: 'bg-yellow-50 border-yellow-200', textColor: 'text-yellow-700', numColor: 'text-yellow-600' },
    { label: 'SLA Breached', key: 'slaBreached', color: 'bg-red-50 border-red-200', textColor: 'text-red-700', numColor: 'text-red-600' },
  ];

  return (
    <div className="pl-1 pr-1 sm:pr-6 min-h-screen">
      {/* Analytics Section */}
      <div className="mb-4 mt-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {analyticsCards.map((card) => (
            <div
              key={card.key}
              className={`border rounded-lg p-4 ${card.color} flex flex-col gap-1`}
            >
              <span className={`text-xs font-medium uppercase tracking-wide ${card.textColor}`}>
                {card.label}
              </span>
              <span className={`text-3xl font-bold ${card.numColor}`}>
                {analyticsData ? analyticsData[card.key] ?? '-' : '-'}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 ml-4">
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="sortOrder"
            value="newest"
            checked={sortOrder === 1}
            onChange={() => handleSortChange(1)}
            className="h-4 w-4 text-blue-600"
          />
          <span>Newest First</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="sortOrder"
            value="oldest"
            checked={sortOrder === 0}
            onChange={() => handleSortChange(0)}
            className="h-4 w-4 text-blue-600"
          />
          <span>Oldest First</span>
        </label>
      </div>

      {enquiries.map((enquiry, index) => (
        <AccordionItem
          key={index}
          data={enquiry}
          index={index}
          isOpen={openIndex === index}
          onToggle={() => handleToggle(index)}
          onClose={() => {
            fetchData(currentPage);
            setOpenIndex(null);
          }}
        />
      ))}

      <div className="flex flex-wrap justify-center items-center mt-4 mb-8 gap-2">
        <button
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          className="px-4 py-2 border rounded disabled:opacity-50"
        >
          Previous
        </button>

        <button
          onClick={() => fetchData(1)}
          className={`px-4 py-2 border rounded ${currentPage === 1 ? 'bg-blue-100' : ''}`}
        >
          1
        </button>

        {currentPage > 1 && (
          <button
            onClick={() => fetchData(currentPage)}
            className="px-4 py-2 border rounded bg-blue-100"
          >
            {currentPage}
          </button>
        )}

        <button
          onClick={handleNextPage}
          disabled={!hasNextPage}
          className="px-4 py-2 border rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
});

export default CheEnquiry;